import React from 'react';
import { X, Calendar, Clock, User, FileText, CheckCircle, MessageSquare } from 'lucide-react';
import { ESTADOS_AUSENCIAS, CATEGORIAS_AUSENCIAS, formatearRangoFechas } from '../../../api/absences';

export default function AbsenceDetails({ absence, onClose }) {
  const estadoConfig = ESTADOS_AUSENCIAS[absence.estado] || ESTADOS_AUSENCIAS.pendiente;
  const categoriaConfig = CATEGORIAS_AUSENCIAS[absence.tipo_categoria] || CATEGORIAS_AUSENCIAS.permiso;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-pink-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">{absence.tipo_nombre}</h2>
            <p className="text-orange-100">Detalles de la Solicitud</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Estado y Categoría */}
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 rounded-full font-semibold ${estadoConfig.bgColor} ${estadoConfig.textColor}`}>
              {estadoConfig.label}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full">
              <span className="text-xl">{categoriaConfig.icon}</span>
              <span className="font-medium">{categoriaConfig.label}</span>
            </span>
          </div>

          {/* Información del Empleado */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Información del Empleado
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-semibold text-gray-800">{absence.empleado_nombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Código</p>
                <p className="font-semibold text-gray-800">{absence.empleado_codigo}</p>
              </div>
              {absence.departamento && (
                <div>
                  <p className="text-sm text-gray-500">Departamento</p>
                  <p className="font-semibold text-gray-800">{absence.departamento}</p>
                </div>
              )}
              {absence.cargo && (
                <div>
                  <p className="text-sm text-gray-500">Cargo</p>
                  <p className="font-semibold text-gray-800">{absence.cargo}</p>
                </div>
              )}
            </div>
          </div>

          {/* Fechas y Duración */}
          <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-5 border border-orange-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Período de Ausencia
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Desde</p>
                <p className="text-lg font-bold text-gray-800">
                  {new Date(absence.fecha_inicio + 'T12:00:00').toLocaleDateString('es-ES', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Hasta</p>
                <p className="text-lg font-bold text-gray-800">
                  {new Date(absence.fecha_fin + 'T12:00:00').toLocaleDateString('es-ES', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Duración</p>
                <p className="text-lg font-bold text-orange-600">
                  {absence.dias_laborables} días laborables
                </p>
                <p className="text-sm text-gray-600">
                  ({absence.dias_solicitados} días calendario)
                </p>
              </div>
            </div>
            {absence.fecha_retorno && (
              <div className="mt-4 pt-4 border-t border-orange-200">
                <p className="text-sm text-gray-600">Retorna el</p>
                <p className="font-semibold text-gray-800">
                  {new Date(absence.fecha_retorno + 'T12:00:00').toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Motivo */}
          {absence.motivo && (
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Motivo
              </h3>
              <p className="text-gray-700">{absence.motivo}</p>
            </div>
          )}

          {/* Suplente */}
          {absence.suplente_nombre && (
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <User className="w-5 h-5" />
                Suplente Asignado
              </h3>
              <p className="font-semibold text-gray-800 mb-2">{absence.suplente_nombre}</p>
              {absence.instrucciones_suplente && (
                <>
                  <p className="text-sm text-gray-600 mb-1">Instrucciones:</p>
                  <p className="text-gray-700">{absence.instrucciones_suplente}</p>
                </>
              )}
            </div>
          )}

          {/* Flujo de Aprobación */}
          {absence.flujo_aprobacion && absence.niveles_aprobacion_total > 0 && (
            <div className="bg-white rounded-xl p-5 border-2 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Flujo de Aprobación
              </h3>

              {/* Timeline visual */}
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                <div className="space-y-6">
                  {absence.flujo_aprobacion.map((nivel, idx) => {
                    const isCompleted = idx < absence.nivel_aprobacion_actual;
                    const isCurrent = idx === absence.nivel_aprobacion_actual;
                    const isPending = idx > absence.nivel_aprobacion_actual;

                    // Buscar aprobación correspondiente
                    const aprobacion = absence.aprobaciones?.find((a) => a.nivel === idx);

                    return (
                      <div key={idx} className="relative flex items-start gap-4 pl-2">
                        {/* Indicador */}
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm z-10 ${
                            isCompleted
                              ? 'bg-green-500 text-white'
                              : isCurrent
                              ? 'bg-orange-500 text-white animate-pulse'
                              : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {isCompleted ? '✓' : idx + 1}
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 pb-6">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p
                              className={`font-semibold mb-1 ${
                                isCompleted ? 'text-green-700' : isCurrent ? 'text-orange-700' : 'text-gray-600'
                              }`}
                            >
                              Nivel {idx + 1}: {nivel.charAt(0).toUpperCase() + nivel.slice(1)}
                            </p>

                            {aprobacion && (
                              <div className="mt-2 space-y-1">
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Aprobado por:</span>{' '}
                                  {aprobacion.aprobador_nombre}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {new Date(aprobacion.fecha).toLocaleString('es-ES')}
                                </p>
                                {aprobacion.comentarios && (
                                  <p className="text-sm text-gray-700 mt-2 p-2 bg-white rounded border border-gray-200">
                                    "{aprobacion.comentarios}"
                                  </p>
                                )}
                              </div>
                            )}

                            {isCurrent && absence.estado === 'pendiente' && (
                              <p className="text-sm text-orange-600 mt-2 font-medium">
                                ⏳ Pendiente de aprobación
                              </p>
                            )}

                            {isPending && (
                              <p className="text-sm text-gray-500 mt-2">En espera...</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Estado final */}
              {absence.estado === 'aprobado' && (
                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-semibold text-lg">
                    ✓ Solicitud Aprobada Completamente
                  </p>
                  {absence.fecha_aprobacion_final && (
                    <p className="text-sm text-green-600 mt-1">
                      {new Date(absence.fecha_aprobacion_final).toLocaleString('es-ES')}
                    </p>
                  )}
                </div>
              )}

              {absence.estado === 'rechazado' && (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 font-semibold text-center">✗ Solicitud Rechazada</p>
                  {absence.motivo_rechazo && (
                    <p className="text-sm text-red-600 mt-2">
                      <span className="font-medium">Motivo:</span> {absence.motivo_rechazo}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Documentos */}
          {absence.total_documentos > 0 && (
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documentos Adjuntos ({absence.total_documentos})
              </h3>
              <p className="text-sm text-gray-600">
                Los documentos se encuentran vinculados al perfil del empleado en el módulo de Documentos.
              </p>
            </div>
          )}

          {/* Información del Sistema */}
          <div className="bg-gray-50 rounded-xl p-5 text-sm text-gray-600 space-y-2">
            <p>
              <span className="font-medium">Solicitado por:</span>{' '}
              {absence.solicitado_por || 'Sistema'}
            </p>
            <p>
              <span className="font-medium">Fecha de solicitud:</span>{' '}
              {new Date(absence.created_at).toLocaleString('es-ES')}
            </p>
            {absence.updated_at !== absence.created_at && (
              <p>
                <span className="font-medium">Última actualización:</span>{' '}
                {new Date(absence.updated_at).toLocaleString('es-ES')}
              </p>
            )}
          </div>

          {/* Información del Tipo */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-5 border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Información del Tipo</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Remunerado:</p>
                <p className="font-semibold text-gray-800">
                  {absence.es_remunerado ? `Sí (${absence.porcentaje_pago}%)` : 'No'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Tipo:</p>
                <p className="font-semibold text-gray-800">{absence.tipo_nombre}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-100 p-4 rounded-b-2xl border-t">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
